import { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Combobox } from './ui/combobox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Loader2, ArrowRight, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useDimensionsList } from '../hooks/useDimension';
import { useAnalyses } from '../hooks/useAnalyses';
import type { DashboardComponent, DashboardTheme } from '../services/dashboard.service';
import { ThemeEditor } from './dashboard/ThemeEditor';
import { resolveInheritedColors } from '@/lib/dashboardTheme';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelOverrides';

type ComponentType = 'MARKDOWN' | 'FILTER' | 'ANALYSIS' | 'INLINE_ANALYSIS';

// A chart either references a saved Analysis or carries its definition inline.
// Everything about presentation applies to both; only the source differs.
const isChartType = (t: ComponentType) => t === 'ANALYSIS' || t === 'INLINE_ANALYSIS';

interface ComponentConfigSheetProps {
  component: DashboardComponent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (component: DashboardComponent) => void;
  onDelete?: (componentId: string) => void;
  // What this panel's colors inherit when it sets none of its own.
  dashboardTheme?: DashboardTheme | null;
}

// Filter panels are superseded by the dashboard's own filter bar, so they are
// no longer offered for new components. The type, its config below and its
// renderer all stay so existing dashboards keep working unmigrated.
const COMPONENT_TYPE_OPTIONS = [
  { value: 'ANALYSIS', label: 'Chart' },
  { value: 'MARKDOWN', label: 'Markdown' },
];

export function ComponentConfigSheet({
  component,
  open,
  onOpenChange,
  onSave,
  onDelete,
  dashboardTheme,
}: ComponentConfigSheetProps) {
  // Local state for editing
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [objectType, setObjectType] = useState<ComponentType>('ANALYSIS');
  const [objectId, setObjectId] = useState<string | undefined>(undefined);
  const [markdownContent, setMarkdownContent] = useState('');
  const [dimensionId, setDimensionId] = useState<string | undefined>(undefined);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [ignoreDateRange, setIgnoreDateRange] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [disableSort, setDisableSort] = useState(false);
  const [inputLabel, setInputLabel] = useState('');
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [theme, setTheme] = useState<DashboardTheme | null>(null);
  const [themeOpen, setThemeOpen] = useState(false);

  const whitelabel = useWhitelabelConfig();
  const inheritedColors = useMemo(
    () => resolveInheritedColors(whitelabel, dashboardTheme),
    [whitelabel, dashboardTheme],
  );

  // Fetch dimensions and analyses for comboboxes
  const { data: dimensionsData, isLoading: dimensionsLoading } = useDimensionsList({
    pageSize: 500,
    enabled: open && objectType === 'FILTER',
  });
  const { data: analysesData, isLoading: analysesLoading } = useAnalyses({
    pageSize: 500,
    enabled: open && objectType === 'ANALYSIS',
  });

  // Reset form when component changes
  useEffect(() => {
    if (component) {
      setTitle(component.title || '');
      setDescription(component.description || '');
      setObjectType((component.object_type as ComponentType) || 'ANALYSIS');
      setObjectId(component.object_id);
      setMarkdownContent(component.markdown_content || '');
      setDimensionId(component.filter_config?.dimension_id);
      setAllowMultiple(component.filter_config?.allow_multiple || false);
      setIgnoreDateRange(component.ignore_date_range || false);
      setHideHeader(component.hide_header || false);
      setDisableSort(component.disable_sort || false);
      setInputLabel(component.filter_config?.input_label || '');
      setTheme(component.theme || null);
      setOverridesOpen(!!(component.title || component.description));
    } else {
      // Reset to defaults for new component
      setTitle('');
      setDescription('');
      setObjectType('ANALYSIS');
      setObjectId(undefined);
      setMarkdownContent('');
      setDimensionId(undefined);
      setAllowMultiple(false);
      setIgnoreDateRange(false);
      setHideHeader(false);
      setDisableSort(false);
      setInputLabel('');
      setOverridesOpen(false);
    }
  }, [component, open]);

  // Build options for comboboxes
  const dimensionOptions = useMemo(() => {
    if (!dimensionsData?.records) return [];
    return dimensionsData.records
      .map((d) => ({
        value: d.id,
        label: d.name || d.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensionsData]);

  const analysisOptions = useMemo(() => {
    if (!analysesData?.records) return [];
    return analysesData.records
      .map((a) => ({
        value: a.id,
        label: a.name || a.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [analysesData]);

  // "Generated chart" is only offered when the component already is one — the
  // definition comes from Ellie, and there is no way to author one by hand here.
  // Without it the Combobox has no option matching the value and renders blank,
  // which is what made this sheet look empty for a generated panel.
  const typeOptions = useMemo(() => {
    if (objectType === 'INLINE_ANALYSIS') {
      return [...COMPONENT_TYPE_OPTIONS, { value: 'INLINE_ANALYSIS', label: 'Generated chart' }];
    }
    // Deprecated, but a component that already is one must still show its own
    // type — otherwise the Combobox matches nothing and renders blank.
    if (objectType === 'FILTER') {
      return [...COMPONENT_TYPE_OPTIONS, { value: 'FILTER', label: 'Filter (deprecated)' }];
    }
    return COMPONENT_TYPE_OPTIONS;
  }, [objectType]);

  const handleSave = () => {
    if (!component) return;

    const updatedComponent: DashboardComponent = {
      ...component,
      title,
      description,
      object_type: objectType,
      object_id: objectType === 'ANALYSIS' ? objectId : undefined,
      // Changing a generated chart into something else leaves its definition
      // behind as dead weight the exporter would still serialize.
      analysis_definition:
        objectType === 'INLINE_ANALYSIS' ? component.analysis_definition : undefined,
      markdown_content: objectType === 'MARKDOWN' ? markdownContent : undefined,
      filter_config:
        objectType === 'FILTER'
          ? {
              filter_type: 'DIMENSION',
              dimension_id: dimensionId,
              allow_multiple: allowMultiple,
              comparator: 'IN',
              input_label: inputLabel || undefined,
            }
          : undefined,
      ignore_date_range: isChartType(objectType) ? ignoreDateRange : undefined,
      hide_header: hideHeader,
      disable_sort: isChartType(objectType) ? disableSort : undefined,
      theme,
    };

    onSave(updatedComponent);
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (component && onDelete) {
      onDelete(component.id);
      onOpenChange(false);
    }
  };

  const isLoading =
    (objectType === 'FILTER' && dimensionsLoading) ||
    (objectType === 'ANALYSIS' && analysesLoading);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* shadcn's default open animation is duration-500, which is most of the
          delay between clicking a panel's gear and being able to use this.
          Scoped here rather than changed in ui/sheet.tsx so other sheets keep
          the motion they were designed with. */}
      <SheetContent className="sm:max-w-md overflow-y-auto data-[state=open]:duration-200">
        <SheetHeader>
          <SheetTitle>Configure Component</SheetTitle>
          <SheetDescription>
            Set up the component's type and configuration.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Component Type */}
          <div className="space-y-2">
            <Label>Component Type</Label>
            <Combobox
              options={typeOptions}
              value={objectType}
              onValueChange={(value) => setObjectType(value as ComponentType)}
              placeholder="Select type..."
            />
          </div>

          {/* Type-specific configuration */}
          {objectType === 'MARKDOWN' && (
            <div className="space-y-2">
              <Label htmlFor="markdown">Markdown Content</Label>
              <Textarea
                id="markdown"
                value={markdownContent}
                onChange={(e) => setMarkdownContent(e.target.value)}
                placeholder="Enter markdown content..."
                rows={8}
              />
            </div>
          )}

          {objectType === 'FILTER' && (
            <>
              <div className="space-y-2">
                <Label>Dimension</Label>
                {dimensionsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading dimensions...
                  </div>
                ) : (
                  <Combobox
                    options={dimensionOptions}
                    value={dimensionId}
                    onValueChange={setDimensionId}
                    placeholder="Select dimension..."
                    emptyText="No dimensions found"
                  />
                )}
                {dimensionId && (
                  <Link
                    to={`/dimensions/${dimensionId}`}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    style={{ cursor: 'pointer' }}
                  >
                    <ArrowRight className="h-3 w-3" />
                    Go to {dimensionOptions.find((d) => d.value === dimensionId)?.label || 'dimension'}
                  </Link>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="input-label">Input Label</Label>
                <Input
                  id="input-label"
                  value={inputLabel}
                  onChange={(e) => setInputLabel(e.target.value)}
                  placeholder="Label shown above the filter input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="allow-multiple">Allow multiple selections</Label>
                <Switch
                  id="allow-multiple"
                  checked={allowMultiple}
                  onCheckedChange={setAllowMultiple}
                />
              </div>
            </>
          )}

          {objectType === 'INLINE_ANALYSIS' && (
            <div className="space-y-2">
              <Label>Chart</Label>
              <p className="text-sm text-muted-foreground m-0">
                This chart was built by Ellie and its definition lives on the
                dashboard, so there is no saved chart to pick. Ask Ellie to change
                it, or switch the type above to use a saved chart instead.
              </p>
            </div>
          )}

          {objectType === 'ANALYSIS' && (
            <>
              <div className="space-y-2">
                <Label>Chart</Label>
                {analysesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading charts...
                  </div>
                ) : (
                  <Combobox
                    options={analysisOptions}
                    value={objectId}
                    onValueChange={setObjectId}
                    placeholder="Select chart..."
                    emptyText="No charts found"
                  />
                )}
                {objectId && (
                  <Link
                    to={`/analytics/charts/${objectId}`}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    style={{ cursor: 'pointer' }}
                  >
                    <ArrowRight className="h-3 w-3" />
                    Go to {analysisOptions.find((a) => a.value === objectId)?.label || 'chart'}
                  </Link>
                )}
              </div>
            </>
          )}

          {/* Custom Title & Description overrides */}
          <Collapsible open={overridesOpen} onOpenChange={setOverridesOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight className={`h-4 w-4 transition-transform ${overridesOpen ? 'rotate-90' : ''}`} />
              Custom Title & Description
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-4">
              <p className="text-xs text-muted-foreground">
                Override the title and description inherited from the linked chart or dimension.
              </p>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Leave blank to use default"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Leave blank to use default"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Display options */}
          <div className="pt-4 border-t space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="hide-header">Hide header</Label>
              <Switch
                id="hide-header"
                checked={hideHeader}
                onCheckedChange={setHideHeader}
              />
            </div>
            {isChartType(objectType) && (
              <>
                {/* Applies to a generated chart as much as a saved one — it is
                    about how the dashboard frames the panel, not where the
                    definition came from. */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="ignore-date-range">Ignore dashboard date range</Label>
                  <Switch
                    id="ignore-date-range"
                    checked={ignoreDateRange}
                    onCheckedChange={setIgnoreDateRange}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="disable-sort">Disable sort</Label>
                  <Switch
                    id="disable-sort"
                    checked={disableSort}
                    onCheckedChange={setDisableSort}
                  />
                </div>
              </>
            )}
          </div>

          {/* Colors. Collapsed by default: overriding one panel's colors is the
              exception, and an open grid of nine pickers would bury the
              settings that are not. */}
          <Collapsible open={themeOpen} onOpenChange={setThemeOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground pt-4 border-t w-full">
              <ChevronRight className={`h-4 w-4 transition-transform ${themeOpen ? 'rotate-90' : ''}`} />
              Colors
              {theme && <span className="text-xs font-normal text-primary">(overridden)</span>}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-4">
                Overrides this panel only. Anything left inherited follows the
                dashboard, then your organization's branding.
              </p>
              <ThemeEditor
                theme={theme}
                onChange={setTheme}
                scope="component"
                inherited={inheritedColors}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>

        <SheetFooter className="mt-8">
          <div className="flex w-full justify-between">
            {onDelete && component && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                Save
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
